import { AlignmentButtons } from '@/components/TestEditor/components/AlignmentButton';
import ColorButton from '@/components/TestEditor/components/ColorButton';
import { FontSizeButton } from '@/components/TestEditor/components/FontSizeButton';
import IconButton from '@/components/TestEditor/components/IconButton';
import { ImageButton } from '@/components/TestEditor/components/ImageButton';
import { LinkButton } from '@/components/TestEditor/components/LinkButton';
import TipsAndTemplate from '@/components/TestEditor/components/TipsAndTemplate';
import VariableButton from '@/components/TestEditor/components/VariableButton';
import { TextEditorStyled } from '@/components/TestEditor/styled';
import { Icon } from '@shopify/polaris';
import { AlertCircleIcon } from '@shopify/polaris-icons';
import { useEffect, useRef, useState } from 'react';
import {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnNumberedList,
  BtnRedo,
  BtnUnderline,
  BtnUndo,
  ContentEditableEvent,
  Editor,
  EditorProvider,
  HtmlButton,
  Separator,
  Toolbar,
} from 'react-simple-wysiwyg';

interface IProps {
  value: string;
  onChange?: (value: string, styledValue?: string) => void;
  onBlur?: (value: string, styledValue?: string) => void;
  disabled?: boolean;
  isRequired?: boolean;
  isHideTipsAndTemplate?: boolean;
  isHideIconButton?: boolean;
  isHideVariableButton?: boolean;
  onlyShowEmojiInIconButton?: boolean;
  defaultError?: string;
  isHideAddImage?: boolean;
}

const TestEditor = ({
  value,
  onChange,
  onBlur,
  disabled,
  isRequired,
  isHideTipsAndTemplate,
  isHideIconButton,
  isHideVariableButton,
  onlyShowEmojiInIconButton,
  defaultError,
  isHideAddImage,
}: IProps) => {
  const [html, setHtml] = useState('');
  const [error, setError] = useState(defaultError);
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const editorRef = useRef<any>(null);
  useEffect(() => {
    setHtml(value);
    if (value?.trim()) setError('');
  }, [value]);

  const handleSetText = (inputStyledValue: string, textValue: string) => {
    const styledValue = inputStyledValue !== '<br>' ? inputStyledValue : '';
    if (styledValue?.trim() && error) setError('');
    onChange?.(textValue, styledValue);
    setHtml(styledValue);
  };

  const handleChange = (event: ContentEditableEvent) => {
    if (disabled) return;
    const value = event.target.value;
    const textValue = event.currentTarget.innerText;
    handleSetText(value, textValue);
  };

  const handleFocus = () => {
    editorRef?.current?.focus();
  };

  const handleBlur = (event: any) => {
    if (disabled) return;
    if (isRequired && !html?.replaceAll('<br>', '')?.replaceAll('&nbsp;', '')?.trim()) {
      setError('This field is required');
    }
    const value = event.target.innerHTML?.replaceAll('&lt;', '<')?.replaceAll('&gt;', '>');
    const textValue = event.target.innerText;
    handleSetText(value, textValue);
    onBlur?.(textValue, html);
  };

  return (
    <TextEditorStyled hasError={!!error}>
      <EditorProvider>
        <Toolbar>
          <BtnUndo />
          <BtnRedo />
          <BtnClearFormatting />
          <BtnBulletList />
          <BtnNumberedList />
          <BtnBold />
          <BtnItalic />
          <BtnUnderline />

          {isHtmlMode ? (
            <div
              onClick={() => {
                setIsHtmlMode(!isHtmlMode);
              }}
            >
              <HtmlButton />
            </div>
          ) : (
            <>
              {/** List custom buttons */}
              <LinkButton onFocus={handleFocus} disabled={disabled} />
              <FontSizeButton disabled={disabled} />
              <ColorButton disabled={disabled} />
              <AlignmentButtons disabled={disabled} onFocus={handleFocus} />
              {isHideAddImage ? null : <ImageButton onFocus={handleFocus} disabled={disabled} />}
              <Separator />
              <div
                onClick={() => {
                  setIsHtmlMode(!isHtmlMode);
                }}
              >
                <HtmlButton />
              </div>
              <Separator />
              {isHideIconButton ? null : (
                <IconButton disabled={disabled} onFocus={handleFocus} onlyShowEmoji={onlyShowEmojiInIconButton} />
              )}
              {isHideVariableButton ? null : <VariableButton disabled={disabled} onFocus={handleFocus} />}
              {isHideTipsAndTemplate ? null : <TipsAndTemplate disabled={disabled} onFocus={handleFocus} />}
            </>
          )}
        </Toolbar>

        <Editor disabled={disabled} ref={editorRef} value={html} onChange={handleChange} onBlur={handleBlur} />
        {error && (
          <p className="d-flex mt-4 text--error">
            <Icon source={AlertCircleIcon} tone="textCritical" /> {error}
          </p>
        )}
      </EditorProvider>
    </TextEditorStyled>
  );
};

export default TestEditor;
